/**
 * Translate handle for an OpeningAnnotation in phase-1.
 *
 * Two instances per opening — one on each side along the wall normal. A
 * drag projects the cursor onto the (center, normal) line and shifts the
 * opening's polygon along the normal accordingly. Live preview via
 * `OpeningAnnotation.setPoints`; on drag end the parent controller pushes
 * an `OpeningGeometrySnapshot` and dispatches `door_window_updated`.
 *
 * The handle is a `SphereMarker` (auto screen-space scaling). It's parented
 * to the controller's helpers group so it sits at small local coords for
 * float32-safe rendering at UTM scale.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';
import { Utils } from '../../../../utils';
import { PixelHitTester } from '../selection/PixelHitTester';
import { OpeningAnnotation } from '../../../../utils/PointoramaAnnotation';
import { OpeningFrame } from './OpeningFrame';

/**
 * Pixel offset from the opening center to the arrow's BASE, along
 * ±normal. Zero so the arrow's tail originates at the opening center
 * and the head extends outward — the rotation ring sits around the
 * shared origin, the arrows pop out through it.
 */
const ARROW_BASE_OFFSET_PX = 0;

/** Arrow shaft + head dimensions, expressed in pixels at the target
 *  screen size. The mesh is built with these as world units and then
 *  scaled per frame to match. Length is set so the arrow's HEAD pokes
 *  out past the rotation ring's outer edge (150 px) — the base lives
 *  at the opening center, the shaft passes through the ring's annulus,
 *  and the head reads clearly outside. */
const ARROW_TOTAL_PX = 195;
const ARROW_HEAD_FRACTION = 0.18;
const ARROW_SHAFT_RADIUS_PX = 2.5;
const ARROW_HEAD_RADIUS_PX = 9;

/** Pixel radius for handle hit-testing — wide enough to cover the visible arrow body. */
const HANDLE_PICK_RADIUS_PX = 22;

/** Handle color: neon green to match the app's accent. */
const HANDLE_COLOR = 0x00ff88;

/** Identifies which side this handle sits on (positive or negative normal direction). */
export type TranslateSide = 'positive' | 'negative';

/**
 * Hit payload reported by `pickAt`. Read back by `beginDrag` so the active
 * side is known throughout the drag.
 */
export interface TranslateHitData {
  side: TranslateSide;
}

export class OpeningTranslateHandle {
  /** Group containing the arrow's shaft + head meshes. Authored along
   *  the local +X axis with TOTAL length 1 (base at 0, tip at +1); we
   *  scale + orient + position this group to fit the wall normal. */
  private arrow: THREE.Group;
  private shaft: THREE.Mesh;
  private head: THREE.Mesh;
  private shaftMaterial: THREE.MeshBasicMaterial;
  private headMaterial: THREE.MeshBasicMaterial;
  private readonly boundUpdate: () => void;

  /** Cached frame center + normal-sign so per-frame `updateLayout` doesn't
   *  re-walk the polygon. Set by `refresh`. */
  private cachedCenter = new THREE.Vector3();
  private cachedNormal = new THREE.Vector3();
  private cachedHelpersGroupPosition = new THREE.Vector3();
  private hasCachedFrame = false;

  /** Where this handle's base (anchor) sits in world coords. Recomputed every frame. */
  private worldPosition = new THREE.Vector3();

  /** Direction the arrow points, in world space (sign matches `side`). */
  private worldDirection = new THREE.Vector3();

  /** Frozen polygon at drag start. Live drag mutates the annotation's points
   *  off this baseline so each frame is independent of accumulated drift. */
  private dragBeforePoints: THREE.Vector3[] | null = null;

  /** Mouse offset along the normal at drag start, in world units. Subtracting
   *  this from the current projection gives the delta to apply. */
  private dragStartProjection = 0;

  /**
   * Frozen frame center + normal captured at drag start. The live frame
   * (mutated as the polygon translates) can't be used to compute the
   * cursor's projection on each frame — the projection origin would
   * shift along with the translation, making `currentProjection -
   * dragStartProjection` measure delta against different origins and
   * causing the polygon to land between the start and end drag points.
   */
  private dragStartCenter = new THREE.Vector3();
  private dragStartNormal = new THREE.Vector3();

  constructor(
    private readonly viewer: Viewer,
    parentGroup: THREE.Group,
    private readonly side: TranslateSide,
    private readonly hitTester: PixelHitTester,
  ) {
    // Arrow authored along local +X: shaft length = 1 - headLen, head length = headLen.
    // We scale uniformly per-frame so the arrow's screen length is `ARROW_TOTAL_PX`.
    const headLen = ARROW_HEAD_FRACTION;
    const shaftLen = 1 - headLen;
    const shaftR = ARROW_SHAFT_RADIUS_PX / ARROW_TOTAL_PX;
    const headR = ARROW_HEAD_RADIUS_PX / ARROW_TOTAL_PX;

    // Shaft: thin cylinder. CylinderGeometry is authored along local +Y;
    // rotate -90° around Z so it points along +X. Position its midpoint
    // at shaftLen / 2 so the base sits at local origin.
    const shaftGeom = new THREE.CylinderGeometry(shaftR, shaftR, shaftLen, 12);
    this.shaftMaterial = new THREE.MeshBasicMaterial({ color: HANDLE_COLOR, depthTest: false, depthWrite: false, transparent: true, opacity: 0.95 });
    this.shaft = new THREE.Mesh(shaftGeom, this.shaftMaterial);
    this.shaft.rotation.z = -Math.PI / 2;
    this.shaft.position.x = shaftLen / 2;

    // Head: cone. Same authoring rotation; positioned at the tip end.
    const headGeom = new THREE.ConeGeometry(headR, headLen, 16);
    this.headMaterial = new THREE.MeshBasicMaterial({ color: HANDLE_COLOR, depthTest: false, depthWrite: false, transparent: true, opacity: 0.95 });
    this.head = new THREE.Mesh(headGeom, this.headMaterial);
    this.head.rotation.z = -Math.PI / 2;
    this.head.position.x = shaftLen + headLen / 2;

    this.arrow = new THREE.Group();
    this.arrow.add(this.shaft);
    this.arrow.add(this.head);
    this.shaft.renderOrder = 1000;
    this.head.renderOrder = 1000;
    parentGroup.add(this.arrow);

    this.boundUpdate = () => this.updateLayout();
    viewer.addEventListener('update', this.boundUpdate);
  }

  /**
   * Cache the latest frame for per-frame layout updates and reposition
   * once immediately. The actual world-position + scale are computed in
   * `updateLayout` (called on every viewer `update` event) so the arrow
   * stays at a constant screen-pixel offset + screen-pixel size as the
   * user zooms / pans.
   */
  refresh(frame: OpeningFrame, helpersGroupPosition: THREE.Vector3): void {
    this.cachedCenter.copy(frame.center);
    this.cachedNormal.copy(frame.normal);
    this.cachedHelpersGroupPosition.copy(helpersGroupPosition);
    this.hasCachedFrame = true;
    this.arrow.visible = true;
    this.updateLayout();
  }

  setVisible(visible: boolean): void {
    this.arrow.visible = visible;
  }

  /**
   * Per-frame layout: arrow base offset + arrow scale are both
   * screen-space pixel constants, so they need to be recomputed
   * whenever the camera changes. Called from the viewer's `update`
   * event.
   */
  private updateLayout(): void {
    if (!this.hasCachedFrame) return;
    const sign = this.side === 'positive' ? +1 : -1;
    const camera = this.viewer.scene.getActiveCamera();
    const size = this.viewer.renderer.getSize(new THREE.Vector2());
    const distance = camera.position.distanceTo(this.cachedCenter);
    const projected = Utils.projectedRadius(1, camera, distance, size.width, size.height);
    if (projected < 1e-6) return;
    const baseOffsetWorld = ARROW_BASE_OFFSET_PX / projected;
    this.worldPosition.copy(this.cachedCenter).addScaledVector(this.cachedNormal, sign * baseOffsetWorld);
    this.worldDirection.copy(this.cachedNormal).multiplyScalar(sign);
    this.arrow.position.copy(this.worldPosition).sub(this.cachedHelpersGroupPosition);
    this.arrow.quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), this.worldDirection);
    const s = ARROW_TOTAL_PX / projected;
    this.arrow.scale.set(s, s, s);
  }

  /**
   * Pixel-distance hit test. Project the arrow's BASE and TIP to screen
   * space and use distance-to-segment so the entire visible arrow body
   * is hittable (not just a point). Falls back to the arrow's midpoint
   * when either endpoint projects offscreen.
   */
  pickAt(event: MouseEvent): TranslateHitData | null {
    const mousePx = this.hitTester.mousePx(event);
    // Tip = base + direction * arrow-screen-length-in-world. Direction is
    // a unit vector; arrow-length-in-world depends on camera distance.
    // Easier: project both base and the world tip we can compute from
    // `arrow.localToWorld(new Vector3(1, 0, 0))`.
    const worldTip = new THREE.Vector3();
    this.arrow.localToWorld(worldTip.set(1, 0, 0));
    const basePx = this.hitTester.projectWorldToPixel({ x: this.worldPosition.x, y: this.worldPosition.y, z: this.worldPosition.z });
    const tipPx = this.hitTester.projectWorldToPixel({ x: worldTip.x, y: worldTip.y, z: worldTip.z });
    if (!basePx || !tipPx) return null;
    const d = PixelHitTester.pointToSegmentDistancePx(mousePx, basePx, tipPx);
    if (d > HANDLE_PICK_RADIUS_PX) return null;
    return { side: this.side };
  }

  /**
   * Start a drag. Snapshots the annotation's current polygon and the
   * cursor's projection onto the wall-normal axis so subsequent
   * `onMouseMove` calls can compute the delta from a stable baseline.
   */
  beginDrag(event: MouseEvent, annotation: OpeningAnnotation, frame: OpeningFrame): void {
    this.dragBeforePoints = annotation.points.map((p) => p.position.clone());
    this.dragStartCenter.copy(frame.center);
    this.dragStartNormal.copy(frame.normal);
    this.dragStartProjection = this.projectMouseOntoNormal(event, this.dragStartCenter, this.dragStartNormal);
  }

  /**
   * Apply the current mouse position as a translation along the wall
   * normal. Updates the annotation's polygon in place so the visible
   * geometry follows the cursor.
   *
   * Uses the FROZEN drag-start center + normal as the projection origin
   * + axis. If we used the live frame instead, the projection origin
   * would track the translation each frame and `currentProjection -
   * dragStartProjection` would measure the delta against shifting
   * origins — the polygon would land somewhere between the cursor and
   * its proper destination.
   */
  onMouseMove(event: MouseEvent, annotation: OpeningAnnotation, _frame: OpeningFrame): void {
    if (!this.dragBeforePoints) return;
    const currentProjection = this.projectMouseOntoNormal(event, this.dragStartCenter, this.dragStartNormal);
    const delta = currentProjection - this.dragStartProjection;
    const offset = this.dragStartNormal.clone().multiplyScalar(delta);
    // Apply the offset to the FROZEN drag-start polygon, not the live
    // annotation points — otherwise each frame would compound on top of
    // the previous frame's translation and drift exponentially.
    const newPoints = this.dragBeforePoints.map((p) => p.clone().add(offset));
    annotation.setPoints(newPoints.map((position) => ({ position, pointCloudId: '' })));
  }

  /**
   * Finish the drag. Returns `{ before, after }` polygons so the parent
   * controller can build an `OpeningGeometrySnapshot`. After this call the
   * handle is ready for a new drag.
   */
  endDrag(annotation: OpeningAnnotation): { before: THREE.Vector3[]; after: THREE.Vector3[] } | null {
    if (!this.dragBeforePoints) return null;
    const before = this.dragBeforePoints;
    const after = annotation.points.map((p) => p.position.clone());
    this.dragBeforePoints = null;
    return { before, after };
  }

  /**
   * Projects the screen-space mouse onto the (center, normal) world line
   * by unprojecting the mouse to a world-space ray and intersecting with
   * the line. For 2D X-Ray ortho (the phase-1 view), the ray is parallel
   * to world -Z; the intersection reduces to a planar projection at the
   * center's Z. Implementation handles both ortho and perspective by
   * computing the ray and intersecting it with the (center, normal)
   * line via the perpendicular foot formula.
   *
   * Takes explicit `center` + `normal` rather than the live frame so
   * the caller can pass the FROZEN drag-start values — necessary so
   * the projection origin doesn't drift along with the in-progress
   * translation.
   *
   * Returns the signed distance along `+normal` from `center`.
   */
  private projectMouseOntoNormal(event: MouseEvent, center: THREE.Vector3, normal: THREE.Vector3): number {
    const camera = this.viewer.scene.getActiveCamera();
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, camera);

    // Perpendicular foot of a line (origin + t * direction) onto another
    // line (frame.center + s * frame.normal). Standard 3D closest-point
    // between two lines; we only want `s` (the projection onto the normal
    // axis).
    const o = ray.ray.origin;
    const d = ray.ray.direction;
    const c = center;
    const n = normal;
    const w0 = new THREE.Vector3().subVectors(o, c);
    const a = d.dot(d);
    const b = d.dot(n);
    const e = n.dot(n);
    const f = d.dot(w0);
    const g = n.dot(w0);
    const denom = a * e - b * b;
    if (Math.abs(denom) < 1e-9) return 0; // parallel — should not happen for a valid ray + horizontal normal
    const s = (a * g - b * f) / denom;
    // Standard closest-points-between-two-lines: with `w = o - c`,
    // the closest point on the (center, normal) line is `c + s*n`, so
    // `s` is the signed distance along `+normal` directly.
    return s;
  }

  dispose(): void {
    this.viewer.removeEventListener('update', this.boundUpdate);
    this.shaft.geometry.dispose();
    this.head.geometry.dispose();
    this.shaftMaterial.dispose();
    this.headMaterial.dispose();
    this.arrow.parent?.remove(this.arrow);
  }
}
